import { isValidPhoneNumber } from "react-phone-number-input";
import { MetaKeys } from "../constants/MetaKeys";
import { DAILY_OPTION, MONTHLY_OPTION, YEARLY_OPTION } from "../constants/notification-key";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Translator } from "../contexts/translator";
import { VALIDITY_IN_OPTION } from "../models/CertificateType";
import { PasswordConfig } from "../models/SecurityPolicyPasswordConfig";
import { characterMoreThanTwoRepetition, characterSequence, digit, domainNameSystemRegex, hexPattern, ipNumber, lowerCase, specialChar, upperCase, urlRegex } from "./regex";
import { emptyString } from "../utilities/regex";

type NumberCondition = 'GREATERTHAN' | 'LESSTHAN' | 'NOTEQUAL' | 'EQUAL' | 'NOTGREATERTHAN' | 'NOTLESSTHAN';

export const validateItemLength = (value: string, length: number, translate: Translator) => {
    let error = (!!value ? requiredValidation(value.trim(), translate) : requiredValidation(value, translate));

    if (value.length !== length) {
        error = translate
            ? translate.byKeyFormatted(TranslationKeys.inputCharacterLengthWithLength, [length])
            : `Input must be ${length} characters in length`;
    }
    return error;
}

/**
 * Validate the input string to ensure disallowed characters are not included.
 * Default disallowed characters include: #%"!?
 *
 * Example usage 1:
 * This will specify that # is allowed in this case
 * validateItemName('Item #1', translate, ['#']);
 *
 * Example usage 2:
 * This uses the default validation rules and doesn't specify any characters to be allowed
 * validateItemName('Item #1', translate);
 *
 * @param value
 * @param translate
 * @param allowedChars
 */
export const validateItemName = (
    value: string,
    translate: Translator,
    allowedChars: string[] = []
) => {
    let error = (!!value ? requiredValidation(value.trim(), translate) : requiredValidation(value, translate));

    // Define disallowed characters
    const disallowedChars = /[#%"!?]/g;

    // Filter out allowed characters from the disallowed set
    const filteredRegex = new RegExp(
        `[${Array.from(new Set(disallowedChars.source.replace(/[\[\]\/]/g, '')))
            .filter(char => !allowedChars.includes(char))
            .join('')}]`,
        'g'
    );

    if (filteredRegex.test(value)) {
        error = translate?.byKey(TranslationKeys.charactersNotAllowed) ?? 'Characters not allowed';
    }

    return error ?? undefined;
};

export const validateControllerName = (value: string, translate: Translator) => {
    let error = (!!value ? requiredValidation(value.trim(), translate) : requiredValidation(value, translate));

    if (value.length > 32) {
        error = translate
        ? translate.byKey(TranslationKeys.maximimumValueWithMaximumValue)
        : 'Maximum length exceeded';
    }
    else if (!/^[a-zA-Z0-9 _-]{1,32}$/i.test(value) && value.length > 0) {
        error = translate
            ? translate.byKey(TranslationKeys.charactersNotAllowed)
            : 'Characters not allowed';
    }
    return error;
}

export const requiredSelectionValidation = (value, translate: Translator) => {
    let error;

    if (!value || value.length === 0) {
        error = translate?.byKey(TranslationKeys.required) ?? 'Required';
    }
    return error;
}

export const isFloat = (val) => {
    let floatRegex = /^-?\d+(?:[.,]\d*?)?$/;
    if (!floatRegex.test(val))
        return false;

    val = parseFloat(val);
    if (isNaN(val))
        return false;
    return true;
}

export const requiredValidation = (value, translate: Translator) => {
    let error;

    if (!value) {
        error = translate?.byKey(TranslationKeys.required) ?? 'Required';
    }

    return error;
}

export const validateMask = (maskArray: string[], translate: Translator) => {
    let value = maskArray.join('');
    let error = requiredValidation(value, translate);

    if (value.length !== 20) {
        error = translate?.byKey(TranslationKeys.maskLengthError) ?? 'Mask must be 20 characters';
    } else if (!hexPattern.test(value)) {
        error = translate?.byKey(TranslationKeys.maskHexError) ?? 'Mask must be hexadecimal';
    }

    return error;
}

export const validateZeroAllowed = (value, min, fieldId, translate: Translator) => {
    if (value === 0 && min <= 0) {
        return undefined
    }
    // TODO: Temporary solution. Replace later
    if (fieldId === '495') {
        const isNumeric = (num: string) => isFloat(num) && num !== '0';
        return isNumeric(value.toString()) ? undefined : 'Valid Number Required'
    }

    return requiredValidation(value, translate)
}

export const validateIntegerWithinBounds = (value: number, minimum: number, maximum: number, translate: Translator, required?: boolean) => {
    let error;
    if (required && (value === undefined || value === null || !value)) {
        error = translate?.byKey(TranslationKeys.required) ?? 'Required';
    }
    else if (Math.floor(value) < value) {
        error = translate.byKey(TranslationKeys.onlyIntegersAllowed) ?? 'Only integers allowed.'
    }
    else if (value < minimum) {
        error = translate.byKeyFormatted(TranslationKeys.minimumValueWithMinimumValue, [minimum]) ?? `Minimum value: ${minimum}`;
    }
    else if (value > maximum) {
        error = translate.byKeyFormatted(TranslationKeys.maximimumValueWithMaximumValue, [maximum]) ?? `Maximum value: ${maximum}`;
    }
    return error;
}

export const validateEndDate = (endDate, startDate, translate: Translator) => {
    let error;
    const sDate = new Date(startDate);
    const eDate = new Date(endDate);

    if (eDate < sDate) {
        error = translate?.byKey(TranslationKeys.beforeStartDate) ?? 'Before start date';
    }
    return error;
}

export const validateRequiredEndDate = (endDate, startDate, translate: Translator) => {
    let error = requiredValidation(endDate, translate);

    if (!error) {
        error = validateEndDate(endDate, startDate, translate);
    }

    return error;
}

export const validateRequiredEndDateWithinOneMonth = (endDate, startDate, translate: Translator) => {
    let error = validateRequiredEndDate(endDate, startDate, translate);

    if (!error && endDate && startDate) {
        const sDate = new Date(startDate);
        const eDate = new Date(endDate);
        const maxEndDate = new Date(sDate);
        maxEndDate.setMonth(maxEndDate.getMonth() + 1);
        if (eDate > maxEndDate) {
            error = translate?.byKey(TranslationKeys.dateRangeExceedsOneMonth) ?? 'Date range must not exceed one month';
        }
    }

    return error;
}

export const validateRequiredEndDateWithinOneWeek = (endDate, startDate, translate: Translator) => {
    let error = validateRequiredEndDate(endDate, startDate, translate);

    if (!error && endDate && startDate) {
        const sDate = new Date(startDate);
        const eDate = new Date(endDate);
        const maxEndDate = new Date(sDate);
        maxEndDate.setDate(maxEndDate.getDate() + 6);
        if (eDate > maxEndDate) {
            error = translate?.byKey(TranslationKeys.dateRangeExceedsOneWeek) ?? 'Date range must not exceed one week';
        }
    }

    return error;
}

export const validateEndTimeRequiredWithDate = (startTime, endTime, startDate, endDate, translate: Translator) => {
    let error;

    if (startTime && !endTime) {
        error = translate?.byKey(TranslationKeys.required) ?? 'Required';
    }

    error = validateEndTimeWithDate(startTime, endTime, startDate, endDate, true, translate)
    return error;
}

export const validateEndTimeWithDate = (startTime, endTime, startDate, endDate, endDateIsRequired: boolean, translate: Translator) => {
    let error;
    const sDate = new Date(startDate);
    const eDate = new Date(endDate);
    const sTime = new Date(startTime);
    const eTime = new Date(endTime)

    if ((!startDate && !endDate) && areTimesEqual(sTime, eTime)) {
        error = translate?.byKey(TranslationKeys.timesCantBeEqual) ?? "Times can't be equal";
    }
    else if ((!startDate && !endDate) && isEndTimeBeforeStartTime(sTime, eTime)) {
        error = translate?.byKey(TranslationKeys.beforeStartTime) ?? 'Before start time';
    }
    else if ((!startDate && endDate) && areTimesEqual(sTime, eTime)) {
        error = translate?.byKey(TranslationKeys.timesCantBeEqual) ?? "Times can't be equal";
    }
    else if ((startDate && !endDate) && areTimesEqual(sTime, eTime)) {
        error = translate?.byKey(TranslationKeys.timesCantBeEqual) ?? "Times can't be equal";
    }
    else if ((!startDate && endDate) && isEndTimeBeforeStartTime(sTime, eTime)) {
        error = translate?.byKey(TranslationKeys.beforeStartTime) ?? 'Before start time';
    }
    else if ((startDate && !endDate) && isEndTimeBeforeStartTime(sTime, eTime)) {
        error = translate?.byKey(TranslationKeys.beforeStartTime) ?? 'Before start time';
    }
    else if (areDatesEqual(sDate, eDate) && areTimesEqual(sTime, eTime)) {
        error = translate?.byKey(TranslationKeys.timesCantBeEqual) ?? "Times can't be equal";
    }
    else if (areDatesEqual(sDate, eDate) && isEndTimeBeforeStartTime(sTime, eTime)) {
        error = translate?.byKey(TranslationKeys.beforeStartTime) ?? 'Before start time';
    }
    // If end time is populated then we need an end date
    // For some reason Portal does not care if start time only is configured so we won't validate that either.
    else if (endDateIsRequired === true && endDate === undefined && (!!endTime)) {
        error = translate?.byKey(TranslationKeys.endDateRequired) ?? 'End date is required';
    }

    return error;
}

export const validateRequiredEndTime = (startTime, endTime, translate: Translator) => {
    const sTime = new Date(startTime);
    const eTime = new Date(endTime);

    let error = requiredValidation(endTime, translate);

    if (areTimesEqual(sTime, eTime)) {
        error = translate?.byKey(TranslationKeys.timesCantBeEqual) ?? "Times can't be equal";
    }
    else if (isEndTimeBeforeStartTime(sTime, eTime)) {
        error =  translate?.byKey(TranslationKeys.beforeStartTime) ??'Before start time';
    }

    return error;
}

export const validateEndTime = (startTime, endTime, translate: Translator) => {
    const sTime = new Date(startTime);
    const eTime = new Date(endTime);
    let error;

    if (startTime && !endTime) {
        error = translate?.byKey(TranslationKeys.required) ?? 'Required';
    }
    else if (areTimesEqual(sTime, eTime)) {
        error = translate?.byKey(TranslationKeys.timesCantBeEqual) ?? "Times can't be equal";
    }
    else if (isEndTimeBeforeStartTime(sTime, eTime)) {
        error = translate?.byKey(TranslationKeys.beforeStartTime) ?? 'Before start time';
    }

    return error;
}

export const validateStartTime = (startTime, endTime, translate: Translator) => {
    let error;

    if (!startTime && endTime) {
        error = translate?.byKey(TranslationKeys.required) ?? 'Required';
    }

    return error;
}

export const validateEmail = (value, translate: Translator, required?: boolean) => {
    let error;
    const emailFormat = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

    if (value && !value.match(emailFormat)) {
        error = translate.byKey(TranslationKeys.notValidEmail) ?? 'Not a valid email'
    }

    if (required && !value) {
        error = required ? requiredValidation(value, translate) : error;
    }

    return error;
}

export const validateConfirmPassword = (pass, value, translate: Translator) => {
    let error = "";

    if (!value)
        error = translate?.byKey(TranslationKeys.required) ?? "Required";
    if (pass && value) {
        if (pass !== value) {
            error = translate?.byKey(TranslationKeys.passwordNotMatched) ?? "Confirm password does not match new password";
        }
    }
    return error;
};

export const validateDevicePassword = (value, translate: Translator, ignoreDigitsCheck: boolean, minLength?: number): string => {
    let error = "";
    const passwordRegex = /(?=.*[0-9])/;
    const minLen = minLength || 6;
    const currPwd = value? value : "";

    // If the password is not provided, return an error.
    if (!currPwd) {
        error = translate.byKey(TranslationKeys.required) ?? "Required";
    }
    // Block on whitespace characters
    else if (currPwd && emptyString.test(currPwd)) {
        error = translate.byKey(TranslationKeys.passwordWhitespaceValidation) ?? "Invalid. Please remove white spaces.";
    }
    // If the password is less than minLen characters, return an error.
    else if (minLength && currPwd.length < minLength) {
        error = translate.byKeyFormatted(TranslationKeys.passwordVariableLengthValidation, [minLen]) ?? `Must be ${minLen} characters long.` ;
    }
    else if (!minLength && currPwd.length < minLen) {
        error = translate?.byKey(TranslationKeys.passwordLengthValidation) ?? `Must be ${minLen} characters long.`;
    }
    // If the password does not match the regex, return an error.
    else if (!!ignoreDigitsCheck && !passwordRegex.test(currPwd)) {
        error = translate.byKey(TranslationKeys.passwordRegexValidation) ?? "Must contain at least one number.";
    }
    // Check if there are more than 2 repeated characters in a row
    else if (characterMoreThanTwoRepetition.test(value)) {
        error = translate ? translate.byKey(TranslationKeys.passwordCharacterRepetition) : "Avoid repeated characters like aaa or 111.";
    }
    // check if there are sequential characters like abc or 123
    else if (characterSequence.test(value)) {
        error = translate ? translate.byKey(TranslationKeys.passwordSequentialCharacters) : "Avoid sequential characters like abc or 123.";
    }
    return error;
}

export const validatePassword = (values, translate: Translator, currentItem?: any, minLength?: number, ignoreDigitsCheck?: boolean) => {
    let error = "";
    const passwordRegex = /(?=.*[0-9])/;
    const minLen = minLength || 6;

    const currPwd = values? values : "";
    let origPwd = currentItem?.details?.password?.origValue || "";

    // If the password is not provided, return an error.
    if (!currPwd) {
        error = translate.byKey(TranslationKeys.required) ?? "Required";
    }
    // If the password is the same as the original password, no need to validate it.
    else if (currPwd && origPwd && currPwd === origPwd) {
        return error;
    }
    // Block on whitespace characters
    else if (currPwd && emptyString.test(currPwd)) {
        error = translate.byKey(TranslationKeys.passwordWhitespaceValidation) ?? "Invalid. Please remove white spaces.";
    }
    // If the password is less than minLen characters, return an error.
    else if (minLength && currPwd.length < minLength) {
        error = translate.byKeyFormatted(TranslationKeys.passwordVariableLengthValidation, [minLen]) ?? `Must be ${minLen} characters long.` ;
    }
    else if (!minLength && currPwd.length < minLen) {
        error = translate?.byKey(TranslationKeys.passwordLengthValidation) ?? `Must be ${minLen} characters long.`;
    }
    // If the password does not match the regex, return an error.
    else if (!!ignoreDigitsCheck && !passwordRegex.test(currPwd)) {
        error = translate.byKey(TranslationKeys.passwordRegexValidation) ?? "Must contain at least one number.";
    }
    return error;
};

export const validateSecurityPolicyPassword = (value, passwordConfig: PasswordConfig, translate: Translator, required?: boolean) => {
    if (required && !value) {
        return translate?.byKey(TranslationKeys.required) ?? "Required";
    }
    let error = "";
    const hasUpperCase = upperCase.test(value);
    const hasLowerCase = lowerCase.test(value);
    const hasDigit = digit.test(value);
    const hasSpecialChar = specialChar.test(value);
    const hasMinLength = value.length >= (passwordConfig.passwordMinLength ? passwordConfig.passwordMinLength : 1); // need to have a minimum length of at least 1

    if (passwordConfig.enforceUppercase && !hasUpperCase) {
        error = translate ? translate.byKey(TranslationKeys.passwordUppercase) : "New password must contain at least one uppercase letter.";
    } else if (passwordConfig.enforceLowercase && !hasLowerCase) {
        error = translate ? translate.byKey(TranslationKeys.passwordLowercase) : "New password must contain at least one lowercase letter."
    } else if (passwordConfig.enforceDigits && !hasDigit) {
        error = translate ? translate.byKey(TranslationKeys.passwordDigit) : "New password must contain at least one digit."
    } else if (passwordConfig.enforceSpecialCharacters && !hasSpecialChar) {
        error = translate ? translate.byKey(TranslationKeys.passwordSpecialCharacter) : "New password must contain at least one special character."
    } else if (passwordConfig.enforcePasswordMinLength && !hasMinLength) {
        error = translate ? translate.byKeyFormatted(TranslationKeys.passwordLength, [passwordConfig.passwordMinLength]) : `New password must be at least ${passwordConfig.passwordMinLength} characters long.`
    } else if (characterMoreThanTwoRepetition.test(value)) {
        error = translate ? translate.byKey(TranslationKeys.passwordCharacterRepetition) : "Avoid repeated characters like aaa or 111.";
    } else if (characterSequence.test(value)) {
        error = translate ? translate.byKey(TranslationKeys.passwordSequentialCharacters) : "Avoid sequential characters like abc or 123.";
    }

    return error;
}

export const validateUsername = (values, translate: Translator) => {
    let error = "";
    const passwordRegex = /^\s*\S[\s\S]*$/;
    if (!values) {
        error = translate?.byKey(TranslationKeys.required) ?? "Required";
    } else if (!passwordRegex.test(values)) {
        error = translate?.byKey(TranslationKeys.passwordWhitespaceValidation) ?? "Invalid. Please remove white spaces.";
    }
    return error;
};

export const validateIpAddress = (value, translate: Translator, required?: boolean) => {
    let error = "";
    const invalidIpRange = value .split(".").map(ip => Number(ip) >= 0 && Number(ip) <= 255 && ip.length < 4).includes(false); // Either a number outside 0-255 range, or too many chars. E.g. 0123

    if(required && !value){
        error = translate?.byKey(TranslationKeys.required) ?? "Required";
    }
    if (value && !ipNumber.test(value)) {
        error = translate?.byKey(TranslationKeys.ipAddressValidation) ?? "Invalid. Must contain a valid IP address."
    }
    else if (invalidIpRange) {
        error = translate?.byKey(TranslationKeys.invalidIpAddressRange) ?? "Invalid. IP Range must be between 0 and 255."
    }
    return error;
}

const areDatesEqual = (startDate: Date, endDate: Date): boolean => {
    let isEqual: boolean = false;

    if (startDate.getDate() === endDate.getDate()
        && startDate.getMonth() === endDate.getMonth()
        && startDate.getFullYear() === endDate.getFullYear()) {
        isEqual = true;
    }

    return isEqual;
}

const areTimesEqual = (startTime: Date, endTime: Date): boolean => {
    let isEqual: boolean = false;

    // Same hours and minutes
    if (startTime.getHours() == endTime.getHours() && startTime.getMinutes() == endTime.getMinutes()) {
        isEqual = true;
    }

    return isEqual;
}

const isEndTimeBeforeStartTime = (startTime: Date, endTime: Date): boolean => {
    const sTime = parseInt(startTime.getHours().toString() + getMinutes(startTime));
    const eTime = parseInt(endTime.getHours().toString() + getMinutes(endTime));
    return eTime < sTime;
}

const getMinutes = (time: Date) => {
    let sMinutes = time.getMinutes().toString();

    // Pad with zero when minutes are less than 10.
    if (time.getMinutes() < 10) {
        sMinutes = "0"+time.getMinutes().toString();
    }

    return sMinutes;
}

export const restrictToNumbersOnly = (e) => {
    if (!e.key.match(/\d/) && !MetaKeys.includes(e.key)) {
        e.preventDefault();
    }
}

export const convertTextPwdToBase64Pwd = (pwd: string) => {
    return (pwd ? btoa(pwd) : null);
}

const MIN_DIGIT_FOR_POSSIBLE_VALID_PHONE_NUMBER = 5;
export const isPhoneNumberValid = (value, required, transale: Translator) => {
    if (!required && (value === undefined || value === '' || value === null)) {
        return '';
    } else if (value.startsWith('+') && !isValidPhoneNumber(value)) {
        return transale.byKey(TranslationKeys.invalidPhoneNumber);
    }
    else if (isValidPhoneNumber(value) || value.length >= MIN_DIGIT_FOR_POSSIBLE_VALID_PHONE_NUMBER) {
        return '';
    }

    return transale.byKey(TranslationKeys.invalidPhoneNumber);
}

//This not an error but a warning
export const isPhoneNumberPossibleValid = (value, translate: Translator) => {
    if (value.length >= MIN_DIGIT_FOR_POSSIBLE_VALID_PHONE_NUMBER && !isValidPhoneNumber(value)) {
        return translate.byKey(TranslationKeys.possibleValidPhoneNumberWarning)
    }

    return "";
}

export const phoneNumberFieldRestriction = (e) => {
    if (!e.key.match(/\d/) && ![" ", "-", "(", ")", "+", "Backspace", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(e.key)) {
        e.preventDefault()
    }
}

export const validateRecurrenceFrequency = (value: string, translate: Translator) => {
    if (value !== DAILY_OPTION.EVERY && value !== DAILY_OPTION.EVERY_WEEKDAY) {
        return translate.byKey(TranslationKeys.pleaseSelectEveryOrEveryWeekday);
    }
    return null;
};

export const validateNumberOfDays = (value: string, translate: Translator) => {
    const parsedValue = parseInt(value, 10);

    if (isNaN(parsedValue)) {
        return translate.byKey(TranslationKeys.pleaseEnterAValidNumberForDays);
    }

    if (parsedValue <= 0 || parsedValue > 31) {
        return translate.byKey(TranslationKeys.numberOfDaysMustBeBetween1And31);
    }

    return null;
};

export const validateMonthlyOption = (value: string, translate: Translator) => {
    if (value !== MONTHLY_OPTION.DAY && value !== MONTHLY_OPTION.THE) {
        return translate.byKey(TranslationKeys.pleaseSelectDayOrThe);
    }
    return null;
};

export const validateEveryMonthNumber = (value: string, translate: Translator) => {
    if (!value) {
        return translate.byKey(TranslationKeys.PleaseEnterMonthValue);
    }

    const parsedValue = parseInt(value, 10);

    if (isNaN(parsedValue) || parsedValue <= 0 || parsedValue > 12) {
        return translate.byKey(TranslationKeys.pleaseEnterAValidMonthNumberBetween1And12);
    }

    return null;
};

export const validateRecur = (value: string, translate: Translator) => {
    const parsedValue = parseInt(value, 10);

    if (isNaN(parsedValue) || parsedValue <= 0) {
        return translate.byKey(TranslationKeys.pleaseEnterRecurrence);
    }

    return null;
};

export const validateDays = (value: string[], translate: Translator) => {
    if (!value || value.length === 0) {
        return translate.byKey(TranslationKeys.pleaseSelectAtLeastOneDay);
    }

    return null;
};

export const validateYearlyOption = (value: string, translate: Translator) => {
    if (value !== YEARLY_OPTION.ON && value !== YEARLY_OPTION.ON_THE) {
        return translate.byKey(TranslationKeys.pleaseSelectOnOrOnThe);
    }
    return null;
};

export const validateValidityInOption = (value: string, translate: Translator) => {
    if (value !== VALIDITY_IN_OPTION.YEARS && value !== VALIDITY_IN_OPTION.DAYS) {
        return translate.byKey(TranslationKeys.required);
    }
    return null;
};

export const validateYearlyRecur = (value: string, translate: Translator) => {
    const parsedValue = parseInt(value, 10);

    if (
        isNaN(parsedValue) ||
        value === "" ||
        value.length > 2 ||
        parsedValue <= 0 ||
        parsedValue > 99
    ) {
        return translate.byKey(TranslationKeys.pleaseEnterAValidNumberForRecurRange1And99);
    }

    return null;
};

export const validateYearlyDay = (value: string, translate: Translator, yearlyOption: string) => {
    if (yearlyOption === YEARLY_OPTION.ON_THE && !value) {
        return null;
    }

    const parsedValue = parseInt(value, 10);

    if (isNaN(parsedValue) || parsedValue <= 0 || parsedValue > 31) {
        return translate.byKey(TranslationKeys.numberOfDaysMustBeBetween1And31);
    }

    return null;
};

export const domainNameSystemListValidation = (value: string, translate: Translator) => {
    // Allow empty input
    if (!value) return undefined;

    const parts = value.split(',').map(part =>
        // Remove optional 'dns:' prefix and leading space
        part.trim().replace(/^dns:\s*/i, '')
    );

    for (const part of parts) {
        if (part.length > 253) {
            return translate.byKey(TranslationKeys.dnsLengthError);
        }
        if (!domainNameSystemRegex.test(part)) {
            return translate.byKey(TranslationKeys.dnsInvalidCharactersError);
        }
        if (part.startsWith('-')) {
            return translate.byKey(TranslationKeys.dnsStartsWithHyphenError);
        }
        if (part.endsWith('-')) {
            return translate.byKey(TranslationKeys.dnsEndsWithHyphenError);
        }
    }

    return undefined;
};

export const internetProtocolListValidation = (value: string, isRequired: boolean, translate: Translator) => {
    // Allow empty input
    if (!value && !isRequired) return undefined;

    const entries = value.split(',').map(part => part.trim());

    for (const entry of entries) {
        const ip = entry.startsWith('ip:') ? entry.slice(3) : entry;
        const error = validateIpAddress(ip, translate, false);
        if (error) return error;
    }

    return undefined;
};

export const uniformResourceIdentifierListValidation = (value: string, translate: Translator) => {
    // Allow empty input
    if (!value) return undefined;

    const entries = value.split(',').map(part => part.trim());

    for (const entry of entries) {
        const uri = entry.startsWith('uri:') ? entry.slice(4) : entry;

        // Must start with http:// or https://
        if (!uri.startsWith('http://') && !uri.startsWith('https://')) {
            return translate.byKey(TranslationKeys.uriPrefixError);
        }

        const match = uri.match(urlRegex);
        if (!match) {
            return translate.byKey(TranslationKeys.uriFormatError);
        }

        // Validate port if present
        const portPart = match[3]; // includes colon, like :443
        if (portPart) {
            const port = parseInt(portPart.slice(1), 10);
            if (isNaN(port) || port < 1 || port > 65535) {
                return translate.byKey(TranslationKeys.uriInvalidPortError);
            }
        }
    }

    return undefined;
};

export const validateCertificate = (value: File | null, translate: Translator) => {
    if (!value) {
        return translate.byKey(TranslationKeys.required);
    }

    return undefined;
};

export const validateMacAddress = (value: string, translate: Translator) => {
    let error = (!!value ? requiredValidation(value.trim(), translate) : requiredValidation(value, translate));
    const regex = /^([0-9a-fA-F]{2}(:[0-9a-fA-F]{2}){5}|[0-9a-fA-F]{2}(-[0-9a-fA-F]{2}){5}|[0-9a-fA-F]{12})$/;

    if (value && value.length > 0 && !regex.test(value)) {
        error = translate ? translate.byKey(TranslationKeys.pleaseEnterAValidMacAddress) : "Please enter a valid mac address.";
    }

    return error;
};

export const validateRssiField = (value: number, translate: Translator) => {
    let error = requiredValidation(value, translate);

    if (value < -128 || value > 128) {
        error = translate.byKey(TranslationKeys.HidPresetValidation)
    }

    return error;
};

export const bleNFCKeyValidation = (value: string, translate: Translator) => {
    const error = requiredValidation(value, translate);

    if (!error && value !== "0" && value.length !== 32 ) {
        return translate.byKey(TranslationKeys.bleNFCKeyValidationMessage);
    }

    return error;
};

/**
 *
 * @param primary - number we want to check
 * @param secondary - number we want to check against
 * @param primaryLabel - label for primary number
 * @param secondaryLabel - label for secondary number
 * @param conditions - what condition needs to be performed equal, less than, etc. more conditions can be added as needed
 * @param translate - translator instance
 * @returns
 */
export const validateTwoNumbers = (primary: number, secondary: number, primaryLabel: string, secondaryLabel: string, conditions: NumberCondition[], translate: Translator) : string | undefined => {
    let error: string | undefined = undefined;

    if (conditions.includes('GREATERTHAN') && primary <= secondary) {
        error = translate.byKeyFormatted(TranslationKeys.primaryMustBeGreaterThanSecondary, [primaryLabel, secondaryLabel]) ?? `${primaryLabel} must be greater than ${secondaryLabel}`;
    }
    if (conditions.includes('LESSTHAN') && primary >= secondary) {
        error = translate.byKeyFormatted(TranslationKeys.primaryMustBeLessThanSecondary, [primaryLabel, secondaryLabel]) ?? `${primaryLabel} must be less than ${secondaryLabel}`;
    }
    if (conditions.includes('NOTEQUAL') && primary === secondary) {
        error = translate.byKeyFormatted(TranslationKeys.primaryMustNotBeEqualToSecondary, [primaryLabel, secondaryLabel]) ?? `${primaryLabel} must not be equal to ${secondaryLabel}`;
    }
    if (conditions.includes('EQUAL') && primary !== secondary) {
        error = translate.byKeyFormatted(TranslationKeys.primaryMustBeEqualToSecondary, [primaryLabel, secondaryLabel]) ?? `${primaryLabel} must be equal to ${secondaryLabel}`;
    }
    if (conditions.includes('NOTGREATERTHAN') && primary > secondary) {
        error = translate.byKeyFormatted(TranslationKeys.primaryMustNotBeGreaterThanSecondary, [primaryLabel, secondaryLabel]) ?? `${primaryLabel} must not be greater than ${secondaryLabel}`;
    }
    if (conditions.includes('NOTLESSTHAN') && primary < secondary) {
        error = translate.byKeyFormatted(TranslationKeys.primaryMustNotBeLessThanSecondary, [primaryLabel, secondaryLabel]) ?? `${primaryLabel} must not be less than ${secondaryLabel}`;
    }

    return error;
}
